import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Users, AlertCircle, CalendarClock, RefreshCw, ChevronRight, ClipboardCheck } from 'lucide-react';
import { toast } from 'sonner';
import { compteRenduService } from '../services/api';

const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
  catch { return d; }
};

/**
 * Tableau de bord de suivi pour l'intervenant.
 *  - Bénéficiaires sans compte-rendu récent
 *  - RDV à reprogrammer (prochain RDV déjà dépassé)
 *
 * Props :
 *  - zone           : filtre optionnel par zone géographique
 *  - onOpenBeneficiaire(beneficiaireId) : navigue vers la fiche du bénéficiaire
 */
const SuiviDashboard = ({ zone = null, onOpenBeneficiaire }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await compteRenduService.dashboard();
      setData(res.data);
    } catch (e) {
      toast.error('Erreur de chargement du tableau de bord');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const byZone = (list) => (zone ? (list || []).filter((x) => x.zone_geographique === zone) : (list || []));

  if (loading) {
    return <div className="text-center py-12 text-slate-500">Chargement du suivi…</div>;
  }
  if (!data) return null;

  const sansCr = byZone(data.sans_cr_recent);
  const aReprogrammer = byZone(data.a_reprogrammer);

  return (
    <div className="space-y-6">
      {/* En-tête + KPI */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <ClipboardCheck className="w-5 h-5 text-blue-600" />
          <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Tableau de bord du suivi
          </h2>
        </div>
        <Button variant="outline" size="sm" className="rounded-xl" onClick={load}>
          <RefreshCw className="w-4 h-4 mr-2" />Actualiser
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <KpiCard icon={Users} color="blue" label="Bénéficiaires actifs" value={data.total_beneficiaires} />
        <KpiCard icon={AlertCircle} color="amber" label={`Sans CR depuis +${data.seuil_jours} j`} value={sansCr.length} />
        <KpiCard icon={CalendarClock} color="red" label="RDV à reprogrammer" value={aReprogrammer.length} />
      </div>

      {/* RDV à reprogrammer */}
      <Section
        title="RDV à reprogrammer"
        icon={CalendarClock}
        accent="red"
        empty="Aucun RDV en retard. Tout est à jour 👌"
        items={aReprogrammer}
        renderItem={(b) => (
          <Row key={b.beneficiaire_id} b={b} onOpen={onOpenBeneficiaire}
            right={<Badge className="bg-red-100 text-red-700 text-xs">Prévu le {fmtDate(b.date_prochain_rdv)}</Badge>} />
        )}
      />

      {/* Sans CR récent */}
      <Section
        title="Bénéficiaires sans compte-rendu récent"
        icon={AlertCircle}
        accent="amber"
        empty="Tous les bénéficiaires ont un suivi récent 🎉"
        items={sansCr}
        renderItem={(b) => (
          <Row key={b.beneficiaire_id} b={b} onOpen={onOpenBeneficiaire}
            right={<Badge className="bg-slate-100 text-slate-600 text-xs">{b.dernier_cr ? `Dernier : ${fmtDate(b.dernier_cr)}` : 'Jamais'}</Badge>} />
        )}
      />
    </div>
  );
};

const KpiCard = ({ icon: Icon, color, label, value }) => {
  const palette = {
    blue: 'from-blue-50 to-blue-100 border-blue-200 text-blue-900 text-blue-700 text-blue-400',
    amber: 'from-amber-50 to-amber-100 border-amber-200 text-amber-900 text-amber-700 text-amber-400',
    red: 'from-red-50 to-red-100 border-red-200 text-red-900 text-red-700 text-red-400',
  }[color].split(' ');
  return (
    <Card className={`bg-gradient-to-br ${palette[0]} ${palette[1]} ${palette[2]} rounded-3xl p-6 shadow-sm border`}>
      <div className="flex items-center justify-between">
        <div>
          <p className={`text-sm font-medium ${palette[4]}`}>{label}</p>
          <p className={`text-3xl font-bold ${palette[3]}`}>{value}</p>
        </div>
        <Icon className={`w-10 h-10 ${palette[5]}`} />
      </div>
    </Card>
  );
};

const ACCENT_ICON = { red: 'text-red-600', amber: 'text-amber-600', blue: 'text-blue-600' };

const Section = ({ title, icon: Icon, accent, items, empty, renderItem }) => (
  <Card className="bg-white border-slate-200 rounded-3xl shadow-sm">
    <CardContent className="p-5">
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`w-5 h-5 ${ACCENT_ICON[accent] || 'text-slate-600'}`} />
        <h3 className="font-bold text-slate-900">{title}</h3>
        <Badge className="bg-slate-100 text-slate-600 text-xs">{items.length}</Badge>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-slate-400 py-4 text-center">{empty}</p>
      ) : (
        <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
          {items.map(renderItem)}
        </div>
      )}
    </CardContent>
  </Card>
);

const Row = ({ b, right, onOpen }) => (
  <button
    type="button"
    onClick={() => onOpen && onOpen(b.beneficiaire_id)}
    className="w-full flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50 hover:bg-blue-50 hover:border-blue-200 transition-colors px-3 py-2 text-left"
  >
    <div className="min-w-0">
      <p className="font-semibold text-slate-900 truncate">{b.prenom} {b.nom}</p>
      {b.zone_geographique && <p className="text-xs text-slate-500">{b.zone_geographique}</p>}
    </div>
    <div className="flex items-center gap-2 shrink-0">
      {right}
      <ChevronRight className="w-4 h-4 text-slate-400" />
    </div>
  </button>
);

export default SuiviDashboard;
